import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme } from "emotion-theming";

import { get, getPaymentOfferInfoConfig } from "../../logics/src/utils/helpers";
import { OverlayLoader } from "components/OverlayLoader";
import { PaymentContext } from "wrapper/PaymentWrapper/context/paymentContext";
import { ExperimentAndSwitchContext } from "wrapper/ExperimentAndSwitchWrapper/context";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { doPaymentHandler } from "./logics/doPaymentHandler";
import { VerifyOtpContext } from "wrapper/VerifyOtpWrapper/context";
import { UserContext } from "wrapper/UserWrapper/context";
import { ApplicationContext } from "wrapper/ApplicationWrapper/context";
import { ErrorModalContext } from "wrapper/ErrorModalWrapper/context";
import { trackPaymentMethodClick, trackPayNowClick, trackRenderedFreshMethods } from "./common/dependencies/tracking";
import { initialPaymentInfoState } from "wrapper/PaymentWrapper/dependencies/constants";
import { ALERT_PAYMENT_MODE_VALUE } from "constants/alert";
import { PAYMENT_OPTION, PAYMENT_SCREENS } from "constants/paymentScreens";

import { PciSdkTypes } from "PCI/utills";
import { PAYMENT_FLOWS, PAYMENT_TYPE } from "constants/payment";
import { PAYMENT_MODES, PAYMENT_MODES as PAYMENT_MODES_RELOADED } from "../../../shared/constants/paymentModes";
import { getStringFromId } from "helpers/vernacular";
import { getEndIconforPaymentMethod } from "helpers/utils/getIcons";

import { getPaymentAlert } from "../../logics/src/utils/getPaymentAlert";

import { allTrackingHandlerForApps } from "helpers/utils/trackingHandler";
import { ACCORDIAN, getPaymentMethodPriority, TRACKING_EVENTS } from "constants/index";

import PCI from "PCI/index";
import UpiMethodWrapper from "./PaymentMethods/UpiMethodWrapper";
import NetBankingWrapper from "./PaymentMethods/NetBankingWrapper";
import CashOnDeliveryWrapper from "./PaymentMethods/CashOnDeliveryWrapper";
import CredWrapper from "./PaymentMethods/CredWrapper";
import PaytmWalletWrapper from "./PaymentMethods/PaytmWalletWrapper";
import GiftcardWrapper from "./PaymentMethods/GiftcardWrapper";
import EmiWrapper from "./PaymentMethods/EmiWrapper";
import SnplWrapper from "./PaymentMethods/SnplWrapper";
import PaymentOfferInfo from "@nykaa/payment-sdk/src/PaymentOfferInfo/mobile";
import { getPriorityBasedSortedOffers } from "../../helpers/index";


interface IpaymentMethod {
  method: string;
  priority: number;
}

interface IpaymentView {
  walletConfig: any;
  giftCardConfig: any;
  orderAmount: number;
  timerCallback: any;
  setUpiDetails: any;
  isGPayIntentAvailable: boolean;
  paymentScreen: any;
  showAllPaymentMethods: boolean;
  newPaymentMethod: string[];
}

/**
 * @description
 * View Component of Payment
 * Payment Methods components are called with wrappers
 * Basic Structure of Payment method
 * Visibility
 * Payment Alert
 * PaymentMethodWrapper
 * PayViaPaymentMethodWrapper
 */
const PaymentView = ({
  walletConfig,
  giftCardConfig,
  orderAmount,
  timerCallback,
  setUpiDetails,
  isGPayIntentAvailable,
  paymentScreen,
  showAllPaymentMethods,
  newPaymentMethod,
}: IpaymentView ) => {
  const history = useHistory();
  const theme = useTheme();
  const query = new URLSearchParams(history.location.search) as any;
  const paymentContext = useContext(PaymentContext);
  const errorModalContext = useContext(ErrorModalContext);
  const { PCI: PciExperiment, NewPaymentFailure, ConvenienceCharges } = useContext(ExperimentAndSwitchContext);
  const { applicationData } = useContext(ApplicationContext);
  const { userData } = useContext(UserContext);

  const paymentInfo = get(paymentContext, "paymentInfo");
  const cartInfo = get(paymentContext, "cartInfo");
  const offerInfo = get(paymentContext, "offersData");
  const flow = get(paymentContext, "flow");
  const { deviceType, isApp } = applicationData;
  const { isGuest } = userData;
  const openErrorModal = get(errorModalContext, "openErrorModal");
  const setLoading = get(paymentContext, "setLoading");
  const setIsAutoCreateOrderLoading = get(paymentContext, "setIsAutoCreateOrderLoading");
  const loading = get(paymentContext, "loading");
  const mobileNumber = get(paymentContext, "mobileNumber");
  const doPaymentApi = get(paymentContext, "doPaymentApi");
  const credPayData = get(paymentContext, "credPayData");
  const loadingParams = get(paymentContext, "loadingParams");
  const setIsPaymentProcessing = get(paymentContext, "setIsPaymentProcessing");
  const failureFlowEnabled = NewPaymentFailure?.switchEnabled && NewPaymentFailure?.experimentEnabled;
  const isFailureUrl = failureFlowEnabled && window?.location?.href?.includes("paymentFailureOrderId");

  const setIsCodMethodSelected = get(paymentContext, "setIsCodMethodSelected");
  const paymentOption = get(paymentContext, "paymentOption");
  const setPaymentOption = get(paymentContext, "setPaymentOption");
  const setActiveMethodForSavedPayments = get(paymentContext, "setActiveMethodForSavedPayments");
  const setCartInfo = get(paymentContext, "setCartInfo");
  const paymentMethodFromQuery = query.get("method");
  const currentScreen = query.get("screen");
  
  const {
    setIsVerifyOtpVisible,
    isOtpVerificationRequired,
    handleCallBackAfterVerification,
  } = useContext(VerifyOtpContext);


  // passing extra parameters from context to doPaymentFactory
  const handleDoPaymentFactory = (method: string, params: any) => {
    const requestParams: any = {
      cartInfo,
      paymentInfo,
      isGuest,
      history,
      flow,
      deviceType,
      isOtpVerificationRequired: isOtpVerificationRequired && flow !== PAYMENT_FLOWS.WALLET, // on wallet screen otpVerification is not needed
      mobileNumber,
      // passing isApp in params for webview.
      isApp,
      pciEnabled: PciExperiment.experimentEnabled,
      offerInfo,
      metaDataTags: [],
      applyCartCharges: !!ConvenienceCharges?.experimentEnabled,
    };

    if (flow === PAYMENT_FLOWS.COD_TO_PREPAID) {
      const query = new URLSearchParams(history.location.search);
      const orderId = query.get("orderId");
      requestParams.orderId = orderId;
    }

    const callbacks = {
      setLoading,
      setIsAutoCreateOrderLoading,
      openErrorModal,
      trackPayNowClick: trackPayNowClick,
      setIsVerifyOtpVisible,
      handleCallBackAfterVerification,
      doPaymentApi,
      setCartInfo,
      setIsPaymentProcessing,
      history,
    };
    // passing orderAmount for wallet flow
    if (flow == PAYMENT_FLOWS.WALLET && method == PAYMENT_MODES_RELOADED.GOOGLE_PAY) {
      params.values = {
        ...params.values,
        grandTotal: orderAmount,
      };
    }
    /**
     * added paymentScreen for tracking.
     */
    if (params) {
      params.paymentScreen = paymentScreen;
    }

    const configParams = {
      walletConfig,
      giftCardConfig,
      values: params,
    };
    doPaymentHandler(method, configParams, requestParams, callbacks);
  };

  const [offerRules, setOfferRules] = useState({
    paymentRules: [],
    nonEligiblePaymentRules: [],
  } as any);

  const [newPaymentAccordianMethod, setNewPaymentAccordianMethod] = useState(PAYMENT_MODES.UPI);
  const [newPaymentMethodWithPriority, setNewPaymentMethodWithPriority] = useState([] as IpaymentMethod[]);
  const [offerInfoConfig, setOfferInfoConfig] = useState<any>({});
  const cartRules = get(paymentContext, "offersData");
  const isWalletAccordianOpen = get(paymentContext, "isWalletAccordianOpen");
  const setIsWalletAccordianOpen = get(paymentContext, "setIsWalletAccordianOpen");
  const isFullPaymentByGiftCard = get(giftCardConfig, "isFullPaymentByGiftCard");
  const showStrikedAmount = get(paymentContext, "showStrikedAmount");
  const initialOrderAmount = get(cartInfo, "grandTotal");
  const SelectPaymentOption = getStringFromId(VERNACULAR_STRING_IDS.SELECT_PAYMENT_OPTION, VERNACULAR_DEFAULT_MESSAGES.SELECT_PAYMENT_OPTION);
  /* eslint-disable @typescript-eslint/no-object-literal-type-assertion */
  const contentRefs = useRef({} as {[key: string]: HTMLElement | null});

  /**
   * this function is toggle accordian for new payment method
   */
  const toggleNewPaymentAccordian = (method: string, isDisabled: boolean) => {
    setActiveMethodForSavedPayments(PAYMENT_TYPE.NON_PCI);
    /**
     * this state is for switching between saved and new payment methods.
     */
    setPaymentOption(PAYMENT_OPTION.NEW);

    if (!isDisabled) {
      if (isWalletAccordianOpen) {
        setIsWalletAccordianOpen(false);
      }

      if (newPaymentAccordianMethod === method) {
        setNewPaymentAccordianMethod("");
        setIsCodMethodSelected(false);
      } else {
        /**
         * this is to track saved method click
         */
        trackPaymentMethodClick({ paymentMethod: method, isSaved: false, deviceType, isLoggedIn: !isGuest });
        // apps tracking in new payment method change
        allTrackingHandlerForApps({ event: TRACKING_EVENTS.PAYMENT_METHOD_CLICK, paymentMethod:method, cartInfo, paymentInfo, isSavedPaymentMethod : false, isApp });

        /**
         * To maintain the view of accordian
         * because of animation in accordian to maintain center
         * view we give setTimeOut
         */
        setTimeout(() => {
          contentRefs?.current[method]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 400);

        setNewPaymentAccordianMethod(method);
        setIsCodMethodSelected(method === ACCORDIAN.COD);
      }
    }
  };

  /**
   * This to manage offersBottom sheet
   * this method will be passed as callback
   */
  const toggleOfferBottomSheet = (offerData: any) => {
    let offerVisible = false;
    if (Object.keys(offerData).length) {
      offerVisible = true;
    }

    /* eslint-disable no-shadow */
    const offerInfo = getPaymentOfferInfoConfig({
      offerData,
      theme,
      action: () => toggleOfferBottomSheet({}),
      deviceType,
      isVisible: offerVisible,
    });
    setOfferInfoConfig(offerInfo);
  };


  /**
   * This block is to manage the priority of
   * payment method.
   */
  useEffect(() => {
    if (newPaymentMethod?.length > 0) {
      /**
       * resetting the isCodMethodSelected state.
       */
      setIsCodMethodSelected(false);
      const alertConfig = getPaymentAlert(paymentInfo?.paymentAlertInfo, PAYMENT_MODES.UPI);
      const lessPriority = alertConfig?.lessPriority; // only for upi;
      /**
       * priority for new payment method
       */
      const paymentMethodPriorityConfig = getPaymentMethodPriority();
      let paymentMethodWithPriority = newPaymentMethod.map((method) => {
        /**
         * setting priority based on method
         */
        const methodConfig = {
          method,
          priority: paymentMethodPriorityConfig[method],
          isAlertshowing: false,
        };
        return methodConfig;
      });

      /**
       * for remove the unwanted methods from the list, ex:- simpl and wallet.
       */
      paymentMethodWithPriority = paymentMethodWithPriority.filter(method => method.priority);

      /**
       * In case of UPI Alerting, Upi tile should move to the last.
       */
      const upiIndex = paymentMethodWithPriority.findIndex((paymentMethod) => paymentMethod?.method === PAYMENT_MODES.UPI);
      if (paymentInfo?.paymentAlertInfo?.upi?.bankName?.includes(ALERT_PAYMENT_MODE_VALUE.UPI) && lessPriority && upiIndex > -1) {
        paymentMethodWithPriority[upiIndex].priority = paymentMethodWithPriority[paymentMethodWithPriority?.length - 1]?.priority + 1;
        paymentMethodWithPriority[upiIndex].isAlertshowing = true;
      }

      paymentMethodWithPriority.sort((a, b) => a.priority - b.priority);
      setNewPaymentMethodWithPriority(paymentMethodWithPriority);

      if (!isWalletAccordianOpen && paymentOption !== PAYMENT_OPTION.SAVED) {
        setNewPaymentAccordianMethod(paymentMethodWithPriority[0]?.method);
        /**
         * if first paymentmethod is cod and codCharges are available then
         * show updated cod charge amount in header.
         */
        if (paymentMethodWithPriority[0]?.method === ACCORDIAN.COD) {
          setIsCodMethodSelected(true);
        }
        
        /**
         * to open payment method based on query param.
         */        
        if (paymentMethodFromQuery) {
          /**
           * for maintaining method view.
           */
          contentRefs?.current[paymentMethodFromQuery]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setNewPaymentAccordianMethod(paymentMethodFromQuery);
        }        

      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [newPaymentMethod?.length, paymentInfo?.paymentAlertInfo, paymentScreen]);

  /**
   * tracking for all rendered fresh payment methods
   */
   useEffect(() => {
    if (newPaymentMethodWithPriority?.length > 0 && !loading) {
      const allFreshPaymentMethods = newPaymentMethodWithPriority.map((methodConfig: { method: string}) => methodConfig.method);
      trackRenderedFreshMethods({ allPaymentMethods:allFreshPaymentMethods });
    }
  }, [newPaymentMethodWithPriority, loading]);

  /**
   * check offers is cashback or not.
   * if we have a cashback offer then we are filtering the offer
   * and used without cashback offer.
   */
  useMemo(() => {
    let paymentRules: any = getPriorityBasedSortedOffers(cartRules);

    if (paymentRules?.length || cartRules?.nonEligiblePaymentRules?.length) {
      setOfferRules({
        paymentRules,
        nonEligiblePaymentRules: cartRules?.nonEligiblePaymentRules,
      });
    }
  }, [cartRules]);

  /**
   * need to improve
   * when wallet is open then close all other new payment method &
   */
  useMemo(() => {
    if (isWalletAccordianOpen || paymentOption === PAYMENT_OPTION.SAVED) {
      setNewPaymentAccordianMethod("");
      setIsCodMethodSelected(false);
      setPaymentOption(PAYMENT_OPTION.SAVED);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isWalletAccordianOpen, paymentOption]);


  useEffect(() => {
    if (currentScreen === "newPayment") {
      setPaymentOption(PAYMENT_OPTION.NEW);
    }
  }, [currentScreen]);

  const config = {
    cartRules: offerRules,
    doPayment: handleDoPaymentFactory,
    isAccordianOpened: false,
    toggleNewPaymentAccordian,
    endIcon: getEndIconforPaymentMethod(deviceType, false),
    newPaymentMethod,
    isDisabled: false,
    isGPayIntentAvailable,
    strikedPaymentText: showStrikedAmount ? getStringFromId(
      VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_STRIKED_AMOUNT,
      `Pay ₹${orderAmount} &nbsp; <del> ₹${initialOrderAmount} </del>`,
      {
        oldAmount: initialOrderAmount,
        newAmount: orderAmount,
      }
    ) : "",
    contentRefs,
    newPaymentMethodWithPriority,
    toggleOfferBottomSheet,
  };

  return (
    <div className="layout vertical mrl20 pb15 payment-view" data-testid="lb_paymentMethods">
      {(paymentScreen === PAYMENT_SCREENS.ALL || showAllPaymentMethods) && (
        <h3 className="font-size-16 font-semibold">{SelectPaymentOption}</h3>
      )}
      <div>
        <div className="layout vertical" data-testid="lb_availablePaymentMode">
          {newPaymentMethodWithPriority?.map(
            (methodConfig: { method: string; priority: number }, index) => {
              const method = methodConfig?.method;

              const isAccordianOpened = method === newPaymentAccordianMethod;
              config.isAccordianOpened = isAccordianOpened;
              config.endIcon = getEndIconforPaymentMethod(deviceType, isAccordianOpened);
              config.isDisabled = isFullPaymentByGiftCard && method != ACCORDIAN.GIFT_CARD;

              switch (method) {
                case ACCORDIAN.COD:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <CashOnDeliveryWrapper {...config} />
                    </div>
                  );
                case ACCORDIAN.UPI:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <UpiMethodWrapper {...{ ...config, timerCallback, setUpiDetails }} />
                    </div>
                  );
                case ACCORDIAN.SNPL:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <SnplWrapper {...config} />
                    </div>
                  );
                case ACCORDIAN.CREDIT_CARD:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      {Object.keys(paymentInfo)?.length !==
                        Object.keys(initialPaymentInfoState)?.length && (
                        <PCI
                          paymentContext={paymentContext}
                          sdkType={PciSdkTypes.NEW}
                          deviceType={deviceType}
                          setLoading={setLoading}
                          paymentScreen={paymentScreen}
                          {...config}
                        />
                      )}
                    </div>
                  );
                case ACCORDIAN.NETBANKING:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <NetBankingWrapper {...config} />
                    </div>
                  );
                case ACCORDIAN.EMI:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <EmiWrapper {...config} />
                    </div>
                  );
                case ACCORDIAN.GIFT_CARD:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <GiftcardWrapper {...config} />
                    </div>
                  );
                case ACCORDIAN.PAYTM:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <PaytmWalletWrapper {...config} />
                    </div>
                  );
                case ACCORDIAN.CRED:
                  return (
                    <div
                      key={`newPaymentViewMobile-${index}`}
                      ref={el => (contentRefs.current[method] = el)}
                    >
                      <CredWrapper {...{ ...config, timerCallback, setUpiDetails, credPayData }} />
                    </div>
                  );
                default:
                  return <div key={`newPaymentViewMobile-${index}`}></div>;
              }
            }
          )}
        </div>
      </div>
      {loading && !isFailureUrl && (
        <OverlayLoader isActive={loading} zIndex={3} loadingParams={loadingParams} />
      )}
      {!!Object.keys(offerInfoConfig).length && (
        <PaymentOfferInfo {...offerInfoConfig} history={history} />
      )}
    </div>
  );
};

export default PaymentView;
